import React, { useState, useEffect, useMemo } from "react";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import "./Trainer.css";
import "../Home/publicPages.css";
import "../Home/catalogHero.css";
import AxiosInstance from "../Home/AxiosInstance";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import ProfileImage from "../Home/Profile.png";
import { FiSearch, FiX } from "react-icons/fi";
import {
  CatalogFilters,
  FilterGroup,
  FilterCheckbox,
  toggleFilterValue,
  buildTrainerFilterOptions,
  matchesExactValues,
  matchesAnyExactValues,
} from "../Home/CatalogFilters";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
];

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

export default function Trainer() {
  const navigate = useNavigate();
  const [trainers, setTrainers] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const [filterGenders, setFilterGenders] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterCourses, setFilterCourses] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [sortNewest, setSortNewest] = useState(false);
  const [sortAZ, setSortAZ] = useState(false);
  const trainersPerPage = 9;

  const availableFilters = useMemo(
    () => buildTrainerFilterOptions(trainers),
    [trainers]
  );

  const indexOfLastTrainer = currentPage * trainersPerPage;
  const indexOfFirstTrainer = indexOfLastTrainer - trainersPerPage;
  const currentTrainers = filteredTrainers.slice(
    indexOfFirstTrainer,
    indexOfLastTrainer
  );
  const totalPages = Math.ceil(filteredTrainers.length / trainersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const fetchTrainers = async () => {
      try {
        const res = await AxiosInstance.get("companies/trainers");
        const list = Array.isArray(res.data) ? res.data : [];

        const enriched = await Promise.all(
          list.map(async (trainer) => {
            let city = "";
            let courseNames = [];
            try {
              const addrRes = await AxiosInstance.get(
                `companies/trainer/addresses/${trainer.trainer_id}/`
              );
              const addresses = Array.isArray(addrRes.data) ? addrRes.data : [];
              city = addresses[0]?.city || "";
            } catch {
              /* no address */
            }
            try {
              const courseRes = await AxiosInstance.get(
                `companies/trainer/courses/${trainer.trainer_id}/`
              );
              const courses = Array.isArray(courseRes.data)
                ? courseRes.data
                : [];
              courseNames = courses
                .map((c) => c?.course?.course_name)
                .filter(Boolean);
            } catch {
              /* no courses */
            }
            return { ...trainer, city, courseNames };
          })
        );

        setTrainers(enriched);
        setFilteredTrainers(enriched);
      } catch (err) {
        console.error("Error fetching trainers:", err);
      }
    };

    fetchTrainers();
  }, []);

  useEffect(() => {
    const value = searchTerm.toLowerCase();
    let next = trainers.filter((trainer) => {
      const name = `${trainer.first_name || ""} ${
        trainer.last_name || ""
      }`.toLowerCase();
      const email = (trainer.email || "").toLowerCase();

      const searchMatch =
        !value || name.includes(value) || email.includes(value);

      return (
        searchMatch &&
        matchesExactValues(trainer.gender, filterGenders) &&
        matchesExactValues(trainer.status, filterStatuses) &&
        matchesExactValues(trainer.city, filterCities) &&
        matchesAnyExactValues(trainer.courseNames, filterCourses)
      );
    });

    if (sortAZ) {
      next = [...next].sort((a, b) => {
        const an = `${a.first_name || ""} ${a.last_name || ""}`.trim();
        const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim();
        return an.localeCompare(bn);
      });
    } else if (sortNewest) {
      next = [...next].sort(
        (a, b) => (b.trainer_id || 0) - (a.trainer_id || 0)
      );
    }

    setFilteredTrainers(next);
    setCurrentPage(1);
  }, [
    trainers,
    searchTerm,
    filterGenders,
    filterStatuses,
    filterCourses,
    filterCities,
    sortNewest,
    sortAZ,
  ]);

  const handleSearch = () => {
    setSearchTerm(inputValue.toLowerCase());
  };

  const clearTrainerSearch = () => {
    setInputValue("");
    setSearchTerm("");
  };

  const clearFilters = () => {
    setFilterGenders([]);
    setFilterStatuses([]);
    setFilterCourses([]);
    setFilterCities([]);
    setSortNewest(false);
    setSortAZ(false);
    clearTrainerSearch();
  };

  return (
    <div className="TrainerPage min-vh-100 d-flex flex-column">
      <Header />
      <div className="als-page-hero als-hero-split">
        <div className="als-page-hero-inner">
          <div className="als-hero-copy">
            <h1>Find the right trainer. Grow with experts.</h1>
            <p>
              Learn from experienced instructors who share real-world skills
              and mentorship.
            </p>
          </div>
          <div className="als-hero-media">
            <img
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=520&fit=crop"
              alt="Trainers and learners"
            />
            <div className="als-hero-badge">
              {trainers.length}+ expert trainers
            </div>
          </div>
        </div>
      </div>

      <div className="mp-catalog flex-grow-1">
        <div className="mp-catalog-inner">
          <form
            className="mp-search-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="mp-search-field">
              <FiSearch className="mp-search-icon" />
              <input
                type="text"
                placeholder="Search by trainer name or email"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              {inputValue && (
                <button
                  type="button"
                  className="mp-clear-input"
                  onClick={clearTrainerSearch}
                  aria-label="Clear trainer search"
                >
                  <FiX />
                </button>
              )}
            </div>
            <button type="submit" className="mp-search-btn">
              Search
            </button>
          </form>

          <div className="mp-layout mp-layout-filters">
            <CatalogFilters onClear={clearFilters}>
              {availableFilters.courses.length > 0 && (
                <FilterGroup title="Courses">
                  {availableFilters.courses.map((course) => (
                    <FilterCheckbox
                      key={course}
                      label={course}
                      checked={filterCourses.includes(course)}
                      onChange={() =>
                        setFilterCourses((prev) =>
                          toggleFilterValue(prev, course)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.cities.length > 0 && (
                <FilterGroup title="Location">
                  {availableFilters.cities.map((city) => (
                    <FilterCheckbox
                      key={city}
                      label={city}
                      checked={filterCities.includes(city)}
                      onChange={() =>
                        setFilterCities((prev) =>
                          toggleFilterValue(prev, city)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.genders.length > 0 && (
                <FilterGroup title="Gender">
                  {availableFilters.genders.map((gender) => (
                    <FilterCheckbox
                      key={gender}
                      label={gender}
                      checked={filterGenders.includes(gender)}
                      onChange={() =>
                        setFilterGenders((prev) =>
                          toggleFilterValue(prev, gender)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.statuses.length > 0 && (
                <FilterGroup title="Status">
                  {availableFilters.statuses.map((status) => (
                    <FilterCheckbox
                      key={status}
                      label={status}
                      checked={filterStatuses.includes(status)}
                      onChange={() =>
                        setFilterStatuses((prev) =>
                          toggleFilterValue(prev, status)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
              <FilterGroup title="Sort by">
                <FilterCheckbox
                  label="A-Z"
                  checked={sortAZ}
                  onChange={() => {
                    setSortAZ((prev) => !prev);
                    setSortNewest(false);
                  }}
                />
                <FilterCheckbox
                  label="Newest"
                  checked={sortNewest}
                  onChange={() => {
                    setSortNewest((prev) => !prev);
                    setSortAZ(false);
                  }}
                />
              </FilterGroup>
            </CatalogFilters>

            <div className="mp-main">
              <div className="mp-results-bar">
                <h2>
                  {filteredTrainers.length} trainer
                  {filteredTrainers.length === 1 ? "" : "s"} found
                  {searchTerm ? ` for “${searchTerm}”` : ""}
                </h2>
              </div>

              {currentTrainers.length > 0 ? (
                <div className="mp-list">
                  {currentTrainers.map((trainer) => {
                    const name = `${trainer.first_name || ""} ${
                      trainer.last_name || ""
                    }`.trim();
                    const initial = (name || "?").charAt(0).toUpperCase();
                    const accent = getInitialColor(name);
                    const photo = trainer.image_path
                      ? `${AxiosInstance.defaults.baseURL}/media/${trainer.image_path.replace(
                          /\\/g,
                          "/"
                        )}`
                      : null;

                    return (
                      <article key={trainer.trainer_id} className="mp-list-card">
                        <div className="mp-list-top">
                          <div
                            className="mp-list-logo"
                            style={
                              !photo
                                ? { background: `${accent}1a`, color: accent }
                                : undefined
                            }
                            aria-hidden="true"
                          >
                            {photo ? (
                              <img
                                src={photo}
                                alt=""
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = ProfileImage;
                                }}
                              />
                            ) : (
                              initial
                            )}
                          </div>
                          <div className="mp-list-heading">
                            <h3>{name || "Trainer"}</h3>
                            {trainer.email && (
                              <p className="mp-list-sub">{trainer.email}</p>
                            )}
                            <div className="mp-list-footer">
                              <p className="mp-list-about">
                                {trainer.city ||
                                  trainer.about?.slice(0, 80) ||
                                  "Independent trainer"}
                              </p>
                              <div className="mp-list-action">
                                <button
                                  type="button"
                                  className="mp-view-btn"
                                  onClick={() =>
                                    navigate(`/trainer/${trainer.trainer_id}`)
                                  }
                                >
                                  View profile
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="mp-empty">
                  <h3>No trainers found</h3>
                  <p>Try adjusting your search or filters.</p>
                  <button
                    type="button"
                    className="mp-search-btn"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                </div>
              )}

              {totalPages > 1 && (
                <div className="mp-pagination">
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className={`mp-page-btn ${
                          pageNum === currentPage ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footers />
    </div>
  );
}
